import { useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'

/**
 * Ayuda contextual por pantalla (panel lateral del botón «?» del topbar).
 *
 * Es la versión React del componente del Design System
 * (DesignSystemMecsa/starter/components/help.php) y usa sus mismas clases,
 * así que se ve igual que la ayuda de Proyectos, Operaciones y Documentos.
 *
 * Para documentar una pantalla nueva basta con agregarle su entrada a AYUDA
 * usando la ruta como llave: no hay que tocar la vista.
 */

const AYUDA = {
  '/': {
    titulo: 'Inicio',
    resumen:
      'Resumen del estado de los tickets: cuántos hay abiertos, en proceso y resueltos, y cómo vienen los últimos días.',
    puntos: [
      'Las tarjetas de arriba son los indicadores del momento.',
      'Los gráficos muestran el histórico y el reparto por estado.',
      'Tocá cualquier ticket de la lista para abrirlo.',
    ],
  },
  '/nuevo-ticket': {
    titulo: 'Nuevo ticket',
    resumen: 'Para reportar una incidencia o pedir algo al área de soporte.',
    puntos: [
      'Escribí un título corto y claro: es lo que ve primero quien lo atiende.',
      'En la descripción contá qué pasó, en qué equipo y desde cuándo.',
      'Podés adjuntar una foto o captura de pantalla.',
    ],
    tip: 'Si es algo urgente que deja a alguien sin trabajar, marcá la prioridad y avisá también por chat.',
  },
  '/tickets': {
    titulo: 'Tickets',
    resumen: 'Todos los tickets de la organización, para el equipo de soporte.',
    puntos: [
      'Filtrá por estado, prioridad, categoría o responsable.',
      'En la vista de tablero se arrastra el ticket de una columna a otra para cambiarle el estado.',
      'Al abrir un ticket se registra el tiempo invertido y la solución aplicada.',
    ],
  },
  '/mis-tickets': {
    titulo: 'Mis tickets',
    resumen: 'Los tickets que reportaste vos y cómo van.',
    puntos: [
      'Verde quiere decir resuelto; los demás siguen en proceso.',
      'Entrá al ticket para ver los comentarios y responder.',
    ],
  },
  '/reportes': {
    titulo: 'Reportes',
    resumen:
      'Indicadores de la operación: volumen, tiempos de atención y carga por persona.',
    puntos: [
      'Elegí el rango de fechas arriba.',
      'Se puede exportar a PDF para adjuntarlo a un informe.',
    ],
  },
  '/reportar-incidente': {
    titulo: 'Reportar incidente',
    resumen:
      'Para avisar de cualquier situación que ponga en riesgo la información de la empresa: un correo sospechoso, un equipo perdido, un acceso que no debería existir.',
    puntos: [
      'Reportar a tiempo reduce el daño: avisá aunque no estés seguro.',
      'El asistente le indica los primeros pasos mientras el equipo revisa.',
      'Podés elegir el modo paso a paso o el formulario completo.',
    ],
    tip: 'Si hay datos de clientes o accesos comprometidos, avisá además por teléfono.',
  },
  '/incidentes': {
    titulo: 'Incidentes',
    resumen:
      'Los incidentes de seguridad reportados y en qué estado va cada uno.',
    puntos: [
      'Filtrá por estado, severidad o responsable.',
      'Entrá al incidente para ver el detalle y darle seguimiento.',
    ],
  },
  '/soluciones': {
    titulo: 'Soluciones',
    resumen:
      'Las soluciones que ya se aplicaron antes, para no resolver dos veces el mismo problema.',
    puntos: [
      'Buscá por palabra clave antes de empezar un ticket desde cero.',
      'Al cerrar un ticket, documentá la solución: queda acá.',
    ],
  },
  '/configuracion': {
    titulo: 'Configuración',
    resumen:
      'Catálogos del sistema: categorías, prioridades, estados, orígenes, etiquetas y usuarios.',
    puntos: [
      'Lo que se agrega acá aparece de inmediato en el formulario de tickets.',
      'Borrar un catálogo en uso puede dejar tickets sin clasificar.',
    ],
    tip: 'Esta pantalla es solo para el equipo de soporte.',
  },
  '/mi-cuenta': {
    titulo: 'Mi cuenta',
    resumen: 'Tus datos, tu foto y las notificaciones que querés recibir.',
    puntos: ['Podés activar o desactivar los avisos del navegador.'],
  },
}

const POR_DEFECTO = {
  titulo: 'Ayuda',
  resumen: 'Todavía no hay ayuda escrita para esta pantalla.',
  puntos: [],
}

/** La entrada de ayuda de una ruta (el detalle de un ticket cae en /tickets). */
export const ayudaDeRuta = (ruta) => {
  if (AYUDA[ruta]) return AYUDA[ruta]
  if (ruta.startsWith('/ticket/')) return AYUDA['/tickets']
  return POR_DEFECTO
}

const HelpPanel = () => {
  const { pathname } = useLocation()
  const help = ayudaDeRuta(pathname)

  // Al cambiar de pantalla se cierra el panel: si no, queda abierto con la
  // ayuda de la pantalla anterior.
  useEffect(() => {
    const el = document.getElementById('helpPanel')
    if (!el?.classList.contains('show')) return
    window.bootstrap?.Offcanvas?.getInstance(el)?.hide()
  }, [pathname])

  return (
    <div
      className="offcanvas offcanvas-end help-panel"
      tabIndex="-1"
      id="helpPanel"
      aria-labelledby="helpPanelTitle"
    >
      <div className="offcanvas-header help-panel-header">
        <div>
          <div className="help-eyebrow">Ayuda de esta pantalla</div>
          <h5 className="offcanvas-title" id="helpPanelTitle">
            {help.titulo}
          </h5>
        </div>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Cerrar"
        ></button>
      </div>

      <div className="offcanvas-body help-panel-body">
        <p className="help-resumen">{help.resumen}</p>

        {help.puntos?.length > 0 && (
          <>
            <div className="help-section-title">Qué podés hacer acá</div>
            <ul className="help-list">
              {help.puntos.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </>
        )}

        {help.tip && (
          <div className="help-tip">
            <i className="fas fa-lightbulb"></i>
            <div>{help.tip}</div>
          </div>
        )}

        <div className="help-footer">
          <Link
            to="/ayuda"
            className="help-manual-link"
            data-bs-dismiss="offcanvas"
          >
            <i className="fas fa-book-open"></i> Ver el Centro de Ayuda
          </Link>
          <a
            href="mailto:soporte@grupomecsa.net"
            className="help-support-link"
          >
            <i className="fas fa-headset"></i> Escribir a soporte
          </a>
        </div>
      </div>
    </div>
  )
}

export default HelpPanel
